import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  MdVideoCall,
  MdVisibility,
  MdTimeline,
  MdLocalHospital,
  MdSchool,
  MdHealthAndSafety,
  MdArrowOutward,
  MdVerified,
  MdInsights,
} from 'react-icons/md';
import { HiArrowRight, HiShieldCheck, HiSparkles } from 'react-icons/hi';
import ThreeScene from '../components/ThreeScene';

const Home = () => {
  const gold = '#D4AF37';

  const features = [
    {
      title: 'Knowledge Hub',
      desc: 'Learn eye anatomy, common conditions, warning signs, and care guidance using a clean educational guide.',
      icon: <MdSchool size={28} />,
      link: '/eye-education',
      badge: 'Learn',
    },
    {
      title: 'Tele-Consultation',
      desc: 'Submit symptoms and connect with eye care specialists for professional preliminary review.',
      icon: <MdVideoCall size={28} />,
      link: '/tele-consultation',
      badge: 'Remote',
    },
    {
      title: 'Online Vision Test',
      desc: 'Use a guided Snellen-style digital screening flow to check your visual acuity from any device.',
      icon: <MdVisibility size={28} />,
      link: '/vision-test',
      badge: 'Screen',
    },
    {
      title: 'Success Tracking',
      desc: 'Track recovery progress, symptoms, and post-surgery improvements with intelligent monitoring.',
      icon: <MdTimeline size={28} />,
      link: '/follow-up',
      badge: 'Track',
    },
    {
      title: 'Hospital Finder',
      desc: 'Find nearby eye clinics, hospitals, and emergency guidance when professional care is needed.',
      icon: <MdLocalHospital size={28} />,
      link: '/find-help',
      badge: 'Locate',
    },
  ];

  const stats = [
    { label: 'Screening Flow', value: 'Smart' },
    { label: 'Response Target', value: '24h' },
    { label: 'Care Modules', value: '5+' },
  ];

  const trustItems = [
    {
      title: 'Guided Screening',
      desc: 'Structured digital vision test experience.',
      icon: <MdVisibility size={22} />,
    },
    {
      title: 'Care Awareness',
      desc: 'Clear education for safer decisions.',
      icon: <MdHealthAndSafety size={22} />,
    },
    {
      title: 'Professional Direction',
      desc: 'Emergency guidance and hospital discovery.',
      icon: <MdLocalHospital size={22} />,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.14 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 34 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-black text-white selection:bg-[#D4AF37]/30 selection:text-[#D4AF37]">
      {/* HERO */}
      <section className="relative min-h-[105vh] flex items-center pt-24 overflow-hidden bg-black">
        {/* Premium animated background */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              y: [0, -24, 0],
              x: [0, 12, 0],
              rotate: [0, 5, 0],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-[8%] left-[4%] w-72 h-72 bg-[#D4AF37]/10 blur-[95px] rounded-full"
          />

          <motion.div
            animate={{
              y: [0, 24, 0],
              x: [0, -12, 0],
              rotate: [0, -5, 0],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute bottom-[12%] right-[7%] w-[28rem] h-[28rem] bg-[#AA8A1E]/10 blur-[120px] rounded-full"
          />

          <motion.div
            animate={{
              opacity: [0.08, 0.18, 0.08],
              scale: [1, 1.08, 1],
            }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-[35%] left-[45%] w-96 h-96 bg-white/5 blur-[130px] rounded-full"
          />

          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_65%_55%_at_50%_50%,#000_65%,transparent_100%)]" />

          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#000000_78%)]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 w-full pt-10 md:pt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
            {/* Left content */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-10"
            >
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/8 px-4 py-2 backdrop-blur-xl shadow-[0_0_40px_rgba(212,175,55,0.08)]"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#D4AF37] opacity-60" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#D4AF37]" />
                </span>
                <span className="text-[11px] font-black uppercase tracking-[0.22em] text-[#D4AF37]">
                  Next Generation Eye Care Platform
                </span>
              </motion.div>

              <div className="space-y-6">
                <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black text-white leading-[0.84] tracking-[-0.08em]">
                  Vision
                  <br />
                  <span className="bg-gradient-to-r from-[#AA8A1E] via-[#D4AF37] to-[#F8E282] bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(212,175,55,0.25)]">
                    Evolution.
                  </span>
                </h1>

                <p className="max-w-xl text-base sm:text-lg md:text-xl text-zinc-400 font-semibold leading-relaxed">
                  A premium digital eye-care experience for vision screening, tele-consultation,
                  health education, recovery tracking, and urgent care guidance.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-5">
                <Link
                  to="/vision-test"
                  className="group relative overflow-hidden px-10 py-5 bg-gradient-to-r from-[#AA8A1E] via-[#D4AF37] to-[#F8E282] text-black rounded-2xl font-black text-lg transition-all duration-500 shadow-2xl shadow-yellow-900/30 flex items-center justify-center gap-4 active:scale-95"
                >
                  <span className="absolute inset-0 bg-white/30 translate-x-[-120%] group-hover:translate-x-[120%] transition-transform duration-700 skew-x-12" />
                  <span className="relative">START TEST</span>
                  <HiArrowRight className="relative group-hover:translate-x-1.5 transition-transform duration-300" />
                </Link>

                <Link
                  to="/tele-consultation"
                  className="group px-7 py-5 bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-[#D4AF37]/40 rounded-2xl font-black text-sm uppercase tracking-widest text-zinc-200 transition-all duration-500 flex items-center justify-center gap-4"
                >
                  <span className="w-11 h-11 bg-black border border-white/10 rounded-full flex items-center justify-center text-[#D4AF37] group-hover:scale-110 transition-transform">
                    <MdVideoCall size={23} />
                  </span>
                  Consultation
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-4 sm:gap-8 pt-8 border-t border-white/10">
                {stats.map((stat, idx) => (
                  <motion.div
                    key={idx}
                    whileHover={{ y: -4 }}
                    className="rounded-2xl bg-white/[0.03] border border-white/5 px-4 py-4"
                  >
                    <div className="text-xl sm:text-2xl font-black text-white">{stat.value}</div>
                    <div className="mt-1 text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-[#D4AF37]">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right 3D / animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.82, filter: 'blur(20px)' }}
              animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
              transition={{ duration: 1.2, delay: 0.1 }}
              className="relative hidden lg:block h-[620px]"
            >
              <div className="absolute inset-0 z-10 opacity-75">
                <ThreeScene />
              </div>

              {/* Scanner overlay */}
              <div className="absolute inset-0 z-20 pointer-events-none">
                <motion.div
                  animate={{ top: ['18%', '82%', '18%'] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                  className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37]/90 to-transparent shadow-[0_0_18px_rgba(212,175,55,0.65)] z-30"
                />

                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="w-[470px] h-[470px] rounded-full border border-dashed border-[#D4AF37]/20"
                  />
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
                    className="absolute w-[415px] h-[415px] rounded-full border border-[#D4AF37]/10 border-t-[#D4AF37]/50"
                  />
                  <motion.div
                    animate={{ scale: [1, 1.05, 1], opacity: [0.35, 0.55, 0.35] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                    className="absolute w-[350px] h-[350px] rounded-full border border-[#D4AF37]/10 bg-[#D4AF37]/5 backdrop-blur-[2px]"
                  />
                </div>

                <div className="absolute top-[14%] left-[9%] w-9 h-9 border-t-2 border-l-2 border-[#D4AF37]/45 rounded-tl-lg" />
                <div className="absolute top-[14%] right-[9%] w-9 h-9 border-t-2 border-r-2 border-[#D4AF37]/45 rounded-tr-lg" />
                <div className="absolute bottom-[14%] left-[9%] w-9 h-9 border-b-2 border-l-2 border-[#D4AF37]/45 rounded-bl-lg" />
                <div className="absolute bottom-[14%] right-[9%] w-9 h-9 border-b-2 border-r-2 border-[#D4AF37]/45 rounded-br-lg" />

                <motion.div
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="absolute top-[20%] left-[15%] flex items-center gap-2"
                >
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_8px_rgba(212,175,55,0.8)]" />
                  <span className="text-[9px] font-black text-[#D4AF37] uppercase tracking-[0.2em] font-mono">
                    Tracking Active
                  </span>
                </motion.div>
              </div>

              {/* Floating panels */}
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute top-[8%] right-[-5%] z-30 p-6 bg-black/65 backdrop-blur-2xl border border-[#D4AF37]/20 shadow-2xl shadow-yellow-900/10 rounded-3xl w-60"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 bg-[#D4AF37]/10 text-[#D4AF37] rounded-xl flex items-center justify-center border border-[#D4AF37]/30 shadow-lg shadow-[#D4AF37]/20">
                    <HiShieldCheck size={19} />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-white">
                    Health Guard
                  </span>
                </div>

                <div className="h-1.5 w-full bg-zinc-800 rounded-full overflow-hidden mb-3">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '88%' }}
                    transition={{ duration: 2, delay: 1 }}
                    className="h-full bg-gradient-to-r from-[#AA8A1E] to-[#D4AF37]"
                  />
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-[9px] font-bold text-zinc-400 uppercase">
                    Detection Level
                  </span>
                  <span className="text-[10px] font-black text-[#D4AF37] italic tracking-tighter font-mono">
                    OPTIMAL
                  </span>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 15, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute bottom-[14%] left-[-13%] z-30 p-6 bg-black/65 backdrop-blur-2xl border border-[#D4AF37]/20 shadow-2xl shadow-yellow-900/10 rounded-3xl w-72"
              >
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-xs font-black text-white uppercase tracking-widest mb-1">
                        Live Diagnostics
                      </h4>
                      <p className="text-[9px] text-[#D4AF37]/70 font-bold italic">
                        Processing optical data stream...
                      </p>
                    </div>
                    <span className="flex h-2.5 w-2.5 rounded-full bg-[#D4AF37] animate-pulse shadow-[0_0_10px_#D4AF37]" />
                  </div>

                  <div className="grid grid-cols-8 gap-1 h-10 items-end">
                    {[35, 70, 48, 86, 56, 95, 62, 78].map((height, i) => (
                      <motion.div
                        key={i}
                        animate={{
                          height: [`${height}%`, `${Math.min(height + 18, 100)}%`, `${height}%`],
                        }}
                        transition={{
                          duration: 0.8 + i * 0.08,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                        className="bg-[#D4AF37]/35 rounded-sm"
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="relative z-10 bg-black border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {trustItems.map((item, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -4 }}
                className="group flex items-center gap-4 rounded-2xl border border-white/5 bg-white/[0.03] hover:bg-white/[0.055] hover:border-[#D4AF37]/25 px-5 py-5 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-sm font-black text-white uppercase tracking-widest">
                    {item.title}
                  </h3>
                  <p className="text-xs text-zinc-500 font-semibold mt-1">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-32 relative z-10 bg-[#070707] overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[42rem] h-[42rem] bg-[#D4AF37]/5 blur-[140px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8">
            <div className="space-y-4">
              <span className="inline-flex items-center gap-2 text-[#D4AF37] font-extrabold text-sm uppercase tracking-widest">
                <HiSparkles /> Our Ecosystem
              </span>
              <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight leading-tight">
                Holistic Care
                <br />
                <span className="text-zinc-500">Solutions</span>
              </h2>
            </div>

            <p className="text-zinc-400 font-medium max-w-md leading-relaxed">
              A connected digital environment designed to guide users from awareness to action,
              with elegant flows for screening, learning, consultation, and care discovery.
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((feature, idx) => (
              <motion.div key={idx} variants={itemVariants}>
                <Link
                  to={feature.link}
                  className="group block h-full min-h-[310px] p-8 bg-black/50 backdrop-blur-md rounded-3xl border border-white/5 hover:bg-black/70 hover:border-[#D4AF37]/45 hover:shadow-2xl hover:shadow-[#D4AF37]/10 transition-all duration-500 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/8 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="absolute top-5 right-5">
                    <span className="px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest bg-white/[0.04] border border-white/10 text-zinc-400 group-hover:text-[#D4AF37] group-hover:border-[#D4AF37]/25 transition-all">
                      {feature.badge}
                    </span>
                  </div>

                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-9 transition-all duration-500 group-hover:scale-110 group-hover:bg-[#D4AF37] group-hover:text-black bg-white/5 text-[#D4AF37] border border-[#D4AF37]/20 relative z-10">
                    {feature.icon}
                  </div>

                  <h3 className="text-2xl font-black text-white mb-4 group-hover:text-[#D4AF37] transition-colors relative z-10">
                    {feature.title}
                  </h3>

                  <p className="text-sm font-medium text-zinc-400 leading-relaxed mb-8 relative z-10">
                    {feature.desc}
                  </p>

                  <div className="absolute bottom-8 left-8 right-8 flex items-center justify-between text-sm font-black text-[#D4AF37] opacity-70 group-hover:opacity-100 transition-opacity duration-300 z-10">
                    <span className="uppercase tracking-widest">Explore</span>
                    <MdArrowOutward className="text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            ))}

            <motion.div variants={itemVariants}>
              <div className="h-full min-h-[310px] p-8 rounded-3xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/12 to-black relative overflow-hidden">
                <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-[#D4AF37]/15 blur-3xl" />

                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-9 bg-[#D4AF37] text-black shadow-2xl shadow-yellow-900/20">
                    <MdInsights size={30} />
                  </div>

                  <h3 className="text-2xl font-black text-white mb-4">
                    One Platform.
                    <br />
                    Smarter Vision Care.
                  </h3>

                  <p className="text-sm font-medium text-zinc-400 leading-relaxed mb-8">
                    VisionCarePlus combines digital-first user experience with responsible medical
                    guidance for safer eye health decisions.
                  </p>

                  <Link
                    to="/find-help"
                    className="inline-flex items-center gap-3 text-sm font-black text-[#D4AF37] uppercase tracking-widest"
                  >
                    Get Guidance <HiArrowRight />
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="relative py-28 bg-black overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff04_1px,transparent_1px),linear-gradient(to_bottom,#ffffff04_1px,transparent_1px)] bg-[size:5rem_5rem] opacity-50" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#D4AF37] font-extrabold text-sm uppercase tracking-widest">
              Simple Journey
            </span>
            <h2 className="mt-4 text-4xl md:text-5xl font-black text-white tracking-tight">
              From Screening to Care
            </h2>
            <p className="mt-5 text-zinc-400 font-medium leading-relaxed">
              A clean workflow that helps users understand their eye condition, take basic checks,
              and move toward the right professional support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              ['01', 'Start Screening', 'Begin with a guided digital vision test.'],
              ['02', 'Learn Clearly', 'Understand symptoms and eye health basics.'],
              ['03', 'Request Review', 'Submit information for preliminary consultation.'],
              ['04', 'Find Care', 'Locate nearby hospitals and emergency guidance.'],
            ].map(([num, title, desc], idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: idx * 0.08 }}
                className="relative p-7 rounded-3xl border border-white/5 bg-white/[0.03] hover:bg-white/[0.05] hover:border-[#D4AF37]/25 transition-all"
              >
                <div className="text-5xl font-black text-white/5 mb-8">{num}</div>
                <h3 className="text-lg font-black text-white mb-3">{title}</h3>
                <p className="text-sm text-zinc-500 font-medium leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SAFETY BANNER */}
      <section className="py-14 bg-[#070707] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="relative overflow-hidden px-8 py-10 bg-black border border-[#D4AF37]/20 shadow-2xl shadow-yellow-900/10 rounded-3xl flex flex-col md:flex-row items-center gap-10">
            <div className="absolute right-0 top-0 w-80 h-80 bg-[#D4AF37]/8 blur-[100px] rounded-full" />

            <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-2xl flex items-center justify-center text-[#D4AF37] flex-shrink-0 rotate-3 relative z-10">
              <HiShieldCheck size={34} />
            </div>

            <div className="flex-grow relative z-10">
              <h4 className="text-[#D4AF37] font-extrabold uppercase tracking-widest text-[10px] mb-2">
                Security & Compliance
              </h4>

              <h3 className="text-2xl md:text-3xl font-black text-white mb-3 tracking-tight">
                Professional Medical Responsibility
              </h3>

              <p className="text-sm font-medium text-zinc-400 leading-relaxed max-w-3xl">
                <span className="text-[#D4AF37] font-bold">VisionCarePlus</span> is a
                preliminary screening and guidance platform. It does not replace diagnosis,
                prescriptions, or emergency treatment from a licensed medical professional.
              </p>
            </div>

            <Link
              to="/find-help"
              className="relative z-10 w-full md:w-auto px-8 py-4 bg-[#D4AF37] text-black hover:bg-[#F8E282] shadow-lg shadow-yellow-900/20 rounded-2xl font-black transition-all text-center"
            >
              Emergency Guidance
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28 bg-black overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(212,175,55,0.12),transparent_35%)]" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-[#D4AF37] font-black text-[11px] uppercase tracking-widest mb-7">
            <MdVerified /> Trusted Digital Guidance
          </div>

          <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight leading-tight">
            Take the first step toward
            <br />
            <span className="bg-gradient-to-r from-[#AA8A1E] via-[#D4AF37] to-[#F8E282] bg-clip-text text-transparent">
              better vision care.
            </span>
          </h2>

          <p className="mt-6 text-zinc-400 font-medium max-w-2xl mx-auto leading-relaxed">
            Start with a simple screening, learn what matters, and get guided toward the right
            professional support when needed.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-5">
            <Link
              to="/vision-test"
              className="px-10 py-5 bg-gradient-to-r from-[#AA8A1E] via-[#D4AF37] to-[#F8E282] text-black rounded-2xl font-black text-lg shadow-2xl shadow-yellow-900/25 flex items-center justify-center gap-3"
            >
              Start Vision Test <HiArrowRight />
            </Link>

            <Link
              to="/eye-education"
              className="px-10 py-5 bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-[#D4AF37]/35 text-white rounded-2xl font-black text-lg transition-all"
            >
              Explore Knowledge Hub
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;